<template>
    <div>
      <!-- Supplier Management Dialog -->
      <v-dialog v-model="dialog" max-width="800px">
        <v-card>
          <v-card-title class="red--text d-flex justify-space-between align-center">
            <span>Manage Suppliers</span>
            <v-btn icon @click="closeComponent">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn color="red darken-1" @click="addSupplierDialog = true">Add Supplier</v-btn>
                </v-col>
                <v-col cols="12">
                  <v-data-table
                    :headers="supplierHeaders"
                    :items="suppliers"
                    :search="supplierSearch"
                    class="elevation-1"
                  >
                    <template v-slot:top>
                      <v-text-field
                        v-model="supplierSearch"
                        append-icon="mdi-magnify"
                        label="Search Suppliers"
                        single-line
                        hide-details
                      ></v-text-field>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn icon @click="openEditSupplier(item)" color="blue">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon @click="deleteSupplier(item)" color="red">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>
  
      <!-- Add Supplier Dialog -->
      <v-dialog v-model="addSupplierDialog" max-width="500px">
        <v-card class="rounded-card">
          <v-card-title class="red--text">Add New Supplier</v-card-title>
          <v-card-text>
            <v-form ref="supplierForm">
              <v-text-field
                v-model="newSupplierName"
                label="Supplier Name"
                outlined
                dense
                rounded
                required
              ></v-text-field>
              <v-text-field
                v-model="newSupplierContactPerson"
                label="Contact Person"
                outlined
                dense
                rounded
              ></v-text-field>
              <v-text-field
                v-model="newSupplierAccount"
                label="Supplier Account"
                outlined
                dense
                rounded
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="red darken-1" text @click="addSupplierDialog = false">Cancel</v-btn>
            <v-btn color="red darken-1" @click="addSupplier">Add Supplier</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Edit Supplier Dialog -->
      <v-dialog v-model="editSupplierDialog" max-width="500px">
        <v-card class="rounded-card">
          <v-card-title class="red--text">Edit Supplier</v-card-title>
          <v-card-text>
            <v-form ref="editSupplierForm">
              <v-text-field
                v-model="editingSupplier.supplierName"
                label="Supplier Name"
                outlined
                dense
                rounded
                required
              ></v-text-field>
              <v-text-field
                v-model="editingSupplier.contactPerson"
                label="Contact Person"
                outlined
                dense
                rounded
              ></v-text-field>
              <v-text-field
                v-model="editingSupplier.supplierAccount"
                label="Supplier Account"
                outlined
                dense
                rounded
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="red darken-1" text @click="editSupplierDialog = false">Cancel</v-btn>
            <v-btn color="red darken-1" @click="updateSupplier">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
  import { firebaseApp } from '@/firebaseConfig';
  
  export default {
    data() {
      return {
        dialog: true,
        addSupplierDialog: false,
        editSupplierDialog: false,
        suppliers: [],
        newSupplierName: '',
        newSupplierContactPerson: '',
        newSupplierAccount: '',
        supplierSearch: '',
        supplierHeaders: [
          { text: 'Supplier Name', value: 'supplierName' },
          { text: 'Contact Person', value: 'contactPerson' },
          { text: 'Supplier Account', value: 'supplierAccount' },
          { text: 'Actions', value: 'actions', sortable: false },
        ],
        editingSupplier: {
          id: '',
          supplierName: '',
          contactPerson: '',
          supplierAccount: '',
        },
      };
    },
    methods: {
      closeComponent() {
        this.dialog = false;
        this.$emit('close');
      },
      async fetchSuppliers() {
        const db = getFirestore(firebaseApp);
        try {
          const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
          this.suppliers = suppliersSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            supplierName: docSnap.data()['Supplier Name'],
            contactPerson: docSnap.data()['Contact Person'] || '',
            supplierAccount: docSnap.data()['Supplier Account'] || '',
          }));
        } catch (error) {
          console.error('Error fetching suppliers:', error);
        }
      },
      async addSupplier() {
        if (!this.newSupplierName.trim()) {
          // Supplier Name is compulsory
          return;
        }
        const db = getFirestore(firebaseApp);
        try {
          await addDoc(collection(db, 'suppliers'), {
            'Supplier Name': this.newSupplierName.trim(),
            'Contact Person': this.newSupplierContactPerson.trim(),
            'Supplier Account': this.newSupplierAccount.trim(),
            createdAt: new Date(),
          });
          this.addSupplierDialog = false;
          this.newSupplierName = '';
          this.newSupplierContactPerson = '';
          this.newSupplierAccount = '';
          this.fetchSuppliers();
        } catch (error) {
          console.error('Error adding supplier:', error);
        }
      },
      openEditSupplier(item) {
        this.editingSupplier = { ...item };  // Clone item data for editing
        this.editSupplierDialog = true;
      },
      async updateSupplier() {
        if (!this.editingSupplier.supplierName.trim()) {
          return;
        }
        const db = getFirestore(firebaseApp);
        try {
          await updateDoc(doc(db, 'suppliers', this.editingSupplier.id), {
            'Supplier Name': this.editingSupplier.supplierName.trim(),
            'Contact Person': this.editingSupplier.contactPerson.trim(),
            'Supplier Account': this.editingSupplier.supplierAccount.trim(),
            updatedAt: new Date(),
          });
          this.editSupplierDialog = false;
          this.fetchSuppliers();
        } catch (error) {
          console.error('Error updating supplier:', error);
        }
      },
      async deleteSupplier(item) {
        const db = getFirestore(firebaseApp);
        try {
          await deleteDoc(doc(db, 'suppliers', item.id));
          this.fetchSuppliers();
        } catch (error) {
          console.error('Error deleting supplier:', error);
        }
      },
    },
    mounted() {
      this.fetchSuppliers();
    },
  };
  </script>
  
  <style scoped>
  .rounded-card {
    border-radius: 16px;
  }
  </style>
  